import { createContext, useCallback, useContext, useEffect, useState, ReactNode, useMemo } from "react";
import {
  BillingSnapshot, EffectivePlan, PLAN_FEATURES_FRONT, billingApi, isReadOnly, planLabel,
} from "@/lib/billing-api";
import { useAuth } from "@/lib/auth-context";

interface PlanContextValue {
  loading: boolean;
  effectivePlan: EffectivePlan;
  isTrial: boolean;
  trialDaysLeft: number | null;
  readOnly: boolean;
  features: Record<string, boolean>;
  snapshot: BillingSnapshot | null;
  refresh: () => Promise<void>;
  label: (p?: EffectivePlan) => string;
}

const PlanContext = createContext<PlanContextValue | null>(null);

function daysLeft(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.ceil((t - Date.now()) / 86400000));
}

export function PlanProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [snapshot, setSnapshot] = useState<BillingSnapshot | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (status !== "authenticated") return;
    setLoading(true);
    try {
      const snap = await billingApi.snapshot();
      setSnapshot(snap);
    } catch {
      // Failure here shouldn't crash the app — fall back to a permissive
      // default until the next refresh.
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    if (status === "unauthenticated") {
      setSnapshot(null);
      setLoading(false);
      return;
    }
    if (status === "authenticated") {
      refresh();
    }
  }, [status, refresh]);

  const value = useMemo<PlanContextValue>(() => {
    const effective: EffectivePlan = snapshot?.effective_plan ?? "trial";
    const flags = PLAN_FEATURES_FRONT[effective];
    const features: Record<string, boolean> = {};
    for (const f of [
      "scan", "outfits", "loans", "reports", "team",
      "audit_log", "csv_import", "share_links_unlimited",
      "share_links_basic", "core_catalog",
    ]) {
      features[f] = flags.has(f);
    }
    return {
      loading,
      effectivePlan: effective,
      isTrial: effective === "trial",
      trialDaysLeft: daysLeft(snapshot?.subscription?.trial_end),
      readOnly: isReadOnly(effective),
      features,
      snapshot,
      refresh,
      label: (p?: EffectivePlan) => planLabel(p ?? effective),
    };
  }, [snapshot, loading, refresh]);

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

export function usePlan(): PlanContextValue {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error("usePlan must be used inside <PlanProvider>");
  return ctx;
}
